(() => {
  if (customElements.get('aaraamya-videos')) return;

  class AaraamyaVideos extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.avc__track');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.cardVideoSlides = this.slides.filter((slide) => slide.querySelector('.avc__card-video'));
      this.mediaSlides = this.slides.filter((slide) => slide.querySelector('[data-viewer-template]'));
      this.modal = this.querySelector('[data-modal]');
      this.viewer = this.querySelector('[data-viewer]');
      this.previousArrow = this.querySelector('.avc__arrow--previous');
      this.nextArrow = this.querySelector('.avc__arrow--next');
      this.arrows = this.querySelector('.avc__arrows');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.visibleRatios = new Map();
      this.currentModalIndex = -1;
      this.lastFocused = null;
      this.touchStartX = null;
      this.autoplayAdvanceTarget = null;
      this.autoplayActiveSlide = null;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      if (!this.track || !this.slides.length) return;
      this.bindEvents();
      this.observeVideos();
      this.updateArrows();
    }

    disconnectedCallback() {
      this.pauseAll();
      this.abortController?.abort();
      this.lazyObserver?.disconnect();
      this.playObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (typeof this.cartUnsubscriber === 'function') this.cartUnsubscriber();
      if (this.modal && !this.modal.hidden) this.unlockPage();
    }

    bindEvents() {
      const options = { signal: this.signal };
      this.previousArrow?.addEventListener('click', () => this.scrollByCard(-1), options);
      this.nextArrow?.addEventListener('click', () => this.scrollByCard(1), options);
      this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true, signal: this.signal });
      this.track.addEventListener('pointerdown', () => {
        this.autoplayAdvanceTarget = null;
        this.autoplayActiveSlide = null;
      }, options);
      this.track.addEventListener('wheel', () => {
        this.autoplayAdvanceTarget = null;
        this.autoplayActiveSlide = null;
      }, { passive: true, signal: this.signal });
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.scrollByCard(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.scrollByCard(-1); }
      }, options);

      this.querySelectorAll('[data-open-modal]').forEach((button) => {
        button.addEventListener('click', () => this.openModal(button.closest('[data-slide]')), options);
      });

      this.querySelector('[data-widget-dismiss]')?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.pauseAll();
        this.hidden = true;
        document.querySelectorAll('aaraamya-videos:not([hidden])').forEach((section) => section.playMostVisible?.());
      }, options);

      this.slides.forEach((slide) => {
        const video = slide.querySelector('.avc__card-video');
        if (!video) return;
        video.addEventListener('play', () => this.updateCardState(slide, true), options);
        video.addEventListener('pause', () => this.updateCardState(slide, false), options);
        video.addEventListener('ended', () => this.advanceCardAutoplay(slide), options);
      });

      this.querySelectorAll('[data-close-modal]').forEach((button) => button.addEventListener('click', () => this.closeModal(), options));
      this.querySelector('[data-modal-previous]')?.addEventListener('click', () => this.changeModal(-1), options);
      this.querySelector('[data-modal-next]')?.addEventListener('click', () => this.changeModal(1), options);
      this.querySelector('[data-viewer-toggle]')?.addEventListener('click', () => this.toggleViewerPlayback(), options);
      this.querySelector('[data-viewer-mute]')?.addEventListener('click', () => this.toggleViewerMute(), options);
      this.querySelector('[data-viewer-share]')?.addEventListener('click', () => this.shareViewer(), options);
      this.modal?.addEventListener('keydown', (event) => this.handleModalKeydown(event), options);
      this.modal?.addEventListener('touchstart', (event) => {
        this.touchStartX = event.changedTouches[0]?.clientX;
        this.touchStartedInProducts = Boolean(event.target.closest('.avc__product-list'));
      }, { passive: true, signal: this.signal });
      this.modal?.addEventListener('touchend', (event) => this.handleSwipe(event), { passive: true, signal: this.signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAll();
          this.suspendExternalViewer();
        } else if (this.modal?.hidden) {
          this.playMostVisible();
        } else {
          this.resumeViewer();
        }
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-slide]');
        slide?.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
      }, options);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateArrows());
        this.resizeObserver.observe(this.track);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.cartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === 'product-form' && this.modal && !this.modal.hidden) this.closeModal();
        });
      }
    }

    observeVideos() {
      const videos = this.cardVideoSlides;
      if (!('IntersectionObserver' in window)) {
        videos.slice(0, 2).forEach((slide) => this.loadVideo(slide.querySelector('video')));
        return;
      }

      this.lazyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.loadVideo(entry.target.querySelector('.avc__card-video'));
          this.lazyObserver.unobserve(entry.target);
        });
      }, { rootMargin: '320px 50%' });

      this.playObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.visibleRatios.set(entry.target, entry.intersectionRatio);
          if (!entry.isIntersecting) {
            entry.target.dataset.manualPaused = 'false';
            entry.target.querySelector('.avc__card-video')?.pause();
          }
        });
        this.playMostVisible();
      }, { threshold: [0, .25, .5, .65, .8, 1] });

      videos.forEach((slide) => {
        this.lazyObserver.observe(slide);
        this.playObserver.observe(slide);
      });
    }

    loadVideo(video) {
      if (!video || video.dataset.loaded === 'true') return;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.loaded = 'true';
      video.load();
    }

    playMostVisible() {
      const openViewer = document.querySelector('aaraamya-videos [data-modal]:not([hidden])');
      if (document.hidden || this.dataset.autoplay !== 'true' || this.reducedMotion.matches || openViewer) {
        this.pauseCardVideos();
        return;
      }
      let candidate = null;
      let bestRatio = .54;
      if (this.autoplayAdvanceTarget) {
        const targetRatio = this.visibleRatios.get(this.autoplayAdvanceTarget) || 0;
        const highestRatio = Math.max(0, ...this.visibleRatios.values());
        if (targetRatio > .54 && targetRatio >= highestRatio - .05) {
          candidate = this.autoplayAdvanceTarget;
          this.autoplayActiveSlide = candidate;
          this.autoplayAdvanceTarget = null;
        }
      } else if (this.autoplayActiveSlide) {
        const activeRatio = this.visibleRatios.get(this.autoplayActiveSlide) || 0;
        if (activeRatio > .54 && this.autoplayActiveSlide.dataset.manualPaused !== 'true') {
          candidate = this.autoplayActiveSlide;
        } else {
          this.autoplayActiveSlide = null;
        }
      }
      if (!candidate && !this.autoplayAdvanceTarget) {
        this.visibleRatios.forEach((ratio, slide) => {
          if (ratio > bestRatio && slide.dataset.manualPaused !== 'true') {
            candidate = slide;
            bestRatio = ratio;
          }
        });
        if (candidate) this.autoplayActiveSlide = candidate;
      }
      this.slides.forEach((slide) => {
        const video = slide.querySelector('.avc__card-video');
        if (!video) return;
        if (slide === candidate) this.playCardVideo(slide);
        else video.pause();
      });
    }

    playCardVideo(slide) {
      const video = slide?.querySelector('.avc__card-video');
      if (!video) return;
      this.pauseOtherVideos(video);
      this.loadVideo(video);
      video.muted = true;
      const promise = video.play();
      if (promise) promise.catch(() => this.updateCardState(slide, false));
    }

    advanceCardAutoplay(slide) {
      if (document.hidden || this.dataset.autoplay !== 'true' || this.reducedMotion.matches || (this.modal && !this.modal.hidden)) return;
      const currentIndex = this.cardVideoSlides.indexOf(slide);
      if (currentIndex < 0 || !this.cardVideoSlides.length) return;
      const nextSlide = this.cardVideoSlides[(currentIndex + 1) % this.cardVideoSlides.length];
      const nextVideo = nextSlide.querySelector('.avc__card-video');
      if (!nextVideo) return;

      if (nextSlide === slide) {
        nextVideo.currentTime = 0;
        this.playCardVideo(nextSlide);
        return;
      }

      nextSlide.dataset.manualPaused = 'false';
      this.autoplayActiveSlide = nextSlide;
      this.autoplayAdvanceTarget = nextSlide;
      this.loadVideo(nextVideo);
      nextSlide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      this.playMostVisible();
    }

    pauseAll() {
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    pauseCardVideos() {
      this.cardVideoSlides.forEach((slide) => slide.querySelector('.avc__card-video')?.pause());
    }

    pauseOtherVideos(activeVideo) {
      this.querySelectorAll('video').forEach((video) => {
        if (video !== activeVideo) video.pause();
      });
    }

    updateCardState(slide, playing) {
      slide.classList.toggle('is-playing', playing);
    }

    scrollByCard(direction) {
      const first = this.slides[0];
      if (!first) return;
      this.autoplayAdvanceTarget = null;
      this.autoplayActiveSlide = null;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      this.track.scrollBy({ left: direction * (first.getBoundingClientRect().width + gap), behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
    }

    updateArrows() {
      if (!this.track) return;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      const slidesWidth = this.slides.reduce((total, slide) => total + slide.getBoundingClientRect().width, 0);
      const contentWidth = slidesWidth + (gap * Math.max(0, this.slides.length - 1));
      const isContained = contentWidth <= this.track.clientWidth + 1;
      this.track.classList.toggle('is-contained', isContained);
      if (this.arrows) this.arrows.hidden = isContained;

      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.previousArrow) this.previousArrow.disabled = isContained || this.track.scrollLeft <= 2;
      if (this.nextArrow) this.nextArrow.disabled = isContained || this.track.scrollLeft >= max - 2;
    }

    openModal(slide) {
      if (!this.modal || !slide) return;
      const index = this.mediaSlides.indexOf(slide);
      if (index < 0) return;
      this.lastFocused = document.activeElement;
      document.querySelectorAll('aaraamya-videos').forEach((section) => section.pauseCardVideos?.());
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      this.lockPage();
      this.renderModal(index);
      requestAnimationFrame(() => this.querySelector('.avc__close')?.focus());
    }

    renderModal(index) {
      if (!this.viewer || !this.mediaSlides.length) return;
      this.stopViewerMedia();
      this.currentModalIndex = (index + this.mediaSlides.length) % this.mediaSlides.length;
      const slide = this.mediaSlides[this.currentModalIndex];
      const template = slide.querySelector('[data-viewer-template]');
      this.viewer.replaceChildren(template.content.cloneNode(true));
      const video = this.viewer.querySelector('.avc__viewer-video');
      const iframe = this.viewer.querySelector('.avc__viewer-iframe');
      this.modal.classList.toggle('is-external', Boolean(iframe));

      if (video) {
        this.loadVideo(video);
        video.muted = true;
        this.pauseOtherVideos(video);
        video.addEventListener('play', () => this.syncViewerControls(video));
        video.addEventListener('pause', () => this.syncViewerControls(video));
        video.addEventListener('volumechange', () => this.syncViewerControls(video));
        video.addEventListener('ended', () => this.advanceViewerAutoplay(video));
        this.syncViewerControls(video);
        if (!this.reducedMotion.matches) {
          const promise = video.play();
          if (promise) promise.catch(() => this.syncViewerControls(video));
        }
      }
      if (iframe) iframe.src = this.externalViewerUrl(iframe.dataset.src);
      this.bindProductNavigation();

      const position = this.querySelector('[data-position]');
      if (position) position.textContent = `${this.currentModalIndex + 1} / ${this.mediaSlides.length}`;
      const previous = this.querySelector('[data-modal-previous]');
      const next = this.querySelector('[data-modal-next]');
      const disableNav = this.mediaSlides.length < 2;
      this.modal.classList.toggle('is-single', disableNav);
      if (previous) previous.disabled = disableNav;
      if (next) next.disabled = disableNav;
    }

    changeModal(direction) {
      if (this.mediaSlides.length < 2) return;
      this.renderModal(this.currentModalIndex + direction);
    }

    bindProductNavigation() {
      const list = this.viewer?.querySelector('.avc__product-list');
      const previous = this.viewer?.querySelector('[data-product-previous]');
      const next = this.viewer?.querySelector('[data-product-next]');
      if (!list || !previous || !next) return;

      const update = () => {
        const max = list.scrollWidth - list.clientWidth;
        previous.disabled = list.scrollLeft <= 2;
        next.disabled = list.scrollLeft >= max - 2;
      };
      const move = (direction) => {
        const card = list.querySelector('.avc__product-card');
        const gap = parseFloat(getComputedStyle(list).gap) || 0;
        const distance = (card?.getBoundingClientRect().width || list.clientWidth * .8) + gap;
        list.scrollBy({ left: direction * distance, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      };

      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      list.addEventListener('scroll', update, { passive: true });
      requestAnimationFrame(update);
    }

    advanceViewerAutoplay(video) {
      if (document.hidden || this.modal?.hidden || this.reducedMotion.matches) return;
      if (this.mediaSlides.length > 1) {
        this.changeModal(1);
      } else {
        video.currentTime = 0;
        video.play().catch(() => this.syncViewerControls(video));
      }
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;
      this.stopViewerMedia();
      this.viewer?.replaceChildren();
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      this.unlockPage();
      if (this.lastFocused instanceof HTMLElement) this.lastFocused.focus();
      document.querySelectorAll('aaraamya-videos:not([hidden])').forEach((section) => section.playMostVisible?.());
    }

    stopViewerMedia() {
      this.viewer?.querySelectorAll('video').forEach((video) => video.pause());
      this.viewer?.querySelectorAll('iframe').forEach((iframe) => { iframe.src = 'about:blank'; });
    }

    suspendExternalViewer() {
      const iframe = this.viewer?.querySelector('.avc__viewer-iframe');
      if (iframe && iframe.src !== 'about:blank') iframe.src = 'about:blank';
    }

    resumeViewer() {
      const video = this.viewer?.querySelector('.avc__viewer-video');
      const iframe = this.viewer?.querySelector('.avc__viewer-iframe');
      if (video && !this.reducedMotion.matches) video.play().catch(() => {});
      if (iframe && iframe.src === 'about:blank') iframe.src = this.externalViewerUrl(iframe.dataset.src);
    }

    externalViewerUrl(url) {
      if (!url || this.reducedMotion.matches) return url || '';
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}autoplay=1&mute=1&muted=1`;
    }

    toggleViewerPlayback() {
      const video = this.viewer?.querySelector('.avc__viewer-video');
      if (!video) return;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    }

    toggleViewerMute() {
      const video = this.viewer?.querySelector('.avc__viewer-video');
      if (!video) return;
      video.muted = !video.muted;
    }

    async shareViewer() {
      const slide = this.mediaSlides[this.currentModalIndex];
      const title = slide?.querySelector('.avc__card-title')?.textContent?.trim() || document.title;
      const shareButton = this.querySelector('[data-viewer-share]');
      try {
        if (navigator.share) {
          await navigator.share({ title, url: window.location.href });
        } else if (navigator.clipboard) {
          await navigator.clipboard.writeText(window.location.href);
          if (shareButton) {
            shareButton.setAttribute('aria-label', 'Link copied');
            window.setTimeout(() => shareButton.setAttribute('aria-label', 'Share this video'), 1800);
          }
        }
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn('Unable to share video', error);
      }
    }

    syncViewerControls(video) {
      const playButton = this.querySelector('[data-viewer-toggle]');
      const muteButton = this.querySelector('[data-viewer-mute]');
      playButton?.classList.toggle('is-playing', !video.paused);
      muteButton?.classList.toggle('is-unmuted', !video.muted);
      playButton?.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      muteButton?.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    }

    handleModalKeydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.closeModal(); return; }
      if (event.key === 'ArrowRight') { event.preventDefault(); this.changeModal(1); return; }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.changeModal(-1); return; }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.modal.querySelectorAll('button:not([disabled]), a[href], iframe, [tabindex]:not([tabindex="-1"])')).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    handleSwipe(event) {
      const endX = event.changedTouches[0]?.clientX;
      if (this.touchStartedInProducts) {
        this.touchStartedInProducts = false;
        this.touchStartX = null;
        return;
      }
      if (this.touchStartX == null || endX == null) return;
      const distance = endX - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(distance) < 55) return;
      this.changeModal(distance < 0 ? 1 : -1);
    }

    lockPage() {
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    unlockPage() {
      document.body.style.overflow = this.previousBodyOverflow || '';
    }
  }

  customElements.define('aaraamya-videos', AaraamyaVideos);
})();
